'use client';

import { useEffect, useRef } from 'react';

interface UseVideoViewportOptions {
  threshold?: number;
  rootMargin?: string;
  playImmediately?: boolean;
  isCentered?: boolean;
  centerTolerance?: number; // Percentage of viewport height to consider "centered" (default 20%)
}

export const useVideoViewport = (options: UseVideoViewportOptions = {}) => {
  const {
    threshold = 0.5,
    rootMargin = '0px',
    playImmediately = false,
    isCentered = false,
    centerTolerance = 20,
  } = options;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video is muted for mobile autoplay
    video.muted = true;
    video.playsInline = true; // Critical for iOS

    // If playImmediately is true, start playing right away
    if (playImmediately) {
      video.play().catch((error) => {
        console.log('Video autoplay prevented:', error);
      });
    }

    const checkIfCentered = (element: HTMLVideoElement): boolean => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportCenter = viewportHeight / 2;

      // Calculate the center of the video element
      const elementCenter = rect.top + rect.height / 2;

      // Calculate tolerance range
      const tolerancePixels = (viewportHeight * centerTolerance) / 100;

      // Check if element center is within tolerance of viewport center
      return Math.abs(elementCenter - viewportCenter) < tolerancePixels;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // If isCentered is enabled, only play when video is centered
            if (isCentered) {
              if (checkIfCentered(video)) {
                video.play().catch((error) => {
                  console.log('Video autoplay prevented:', error);
                });
              } else {
                video.pause();
              }
            } else {
              // Video is in viewport - play it
              video.play().catch((error) => {
                // Handle autoplay policy restrictions
                console.log('Video autoplay prevented:', error);
              });
            }
          } else {
            // Video is out of viewport - pause it
            video.pause();
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(video);

    // If isCentered is enabled, also listen to scroll events for more precise centering
    let scrollHandler: (() => void) | null = null;
    if (isCentered) {
      scrollHandler = () => {
        if (video) {
          const rect = video.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

          if (isInViewport && checkIfCentered(video)) {
            video.play().catch((error) => {
              console.log('Video autoplay prevented:', error);
            });
          } else {
            video.pause();
          }
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    return () => {
      observer.disconnect();
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [threshold, rootMargin, playImmediately, isCentered, centerTolerance]);

  return videoRef;
};
